import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import InfoAffordance from "../components/InfoAffordance";
import CcButton from "../components/CcButton";
import ConfirmDialog from "../components/ConfirmDialog";
import type { RootState } from "../../../redux/appState";
import {
  closeConfigurationDetail,
  openConfigModal,
  recalculateConfiguration,
} from "../../../redux/coilConfigurator/actions";
import { selectSelectedConfiguration } from "../../../redux/coilConfigurator/selectors";
import {
  buildCoilLayoutSvgProps,
  buildDetailCalculatedBasisView,
  buildDetailCatalogueEvaluationView,
  buildDetailCoilComparisonView,
  buildDetailEquipmentCardView,
  buildDetailOperatingPointView,
  configurationStatusPillClass,
  createCoilLayoutSvg,
  detailStatusBannerClass,
  formatConfigurationStatusLabel,
} from "../../../web-app";
import "./configuration-detail.css";

interface ConfigurationDetailProps {
  config: ReturnType<typeof selectSelectedConfiguration>;
  onBack: () => void;
  onEdit: (id: string) => void;
  onRecalculate: (id: string) => void;
}

interface AdvisoryItem {
  code: string;
  message: string;
}

function AdvisoryList({
  items,
  emptyMessage,
}: {
  items: AdvisoryItem[];
  emptyMessage: string;
}) {
  if (items.length === 0) {
    return <li className="detail-advisory-empty">{emptyMessage}</li>;
  }

  return (
    <>
      {items.map((item) => (
        <li key={item.code} className="detail-advisory-item">
          <p className="detail-advisory-item__code">{item.code}</p>
          <p className="detail-advisory-item__message">{item.message}</p>
        </li>
      ))}
    </>
  );
}

function DetailStatCard({
  label,
  glossaryKey,
  value,
  unit,
}: {
  label: string;
  glossaryKey: string;
  value: string;
  unit?: string;
}) {
  return (
    <div className="stat-card">
      <span className="stat-card__label">
        {label} <InfoAffordance glossaryKey={glossaryKey} />
      </span>
      <span className="stat-card__value">
        <span>{value}</span>
        {unit ? <span className="stat-card__unit">{unit}</span> : null}
      </span>
    </div>
  );
}

const ConfigurationDetail = ({ config, onBack, onEdit, onRecalculate }: ConfigurationDetailProps) => {
  const [recalculateOpen, setRecalculateOpen] = useState(false);

  const schematicSvg = useMemo(() => {
    if (!config) return null;
    const props = buildCoilLayoutSvgProps(config.recommendation, config);
    return props === null ? null : createCoilLayoutSvg(props);
  }, [config]);

  if (!config) {
    return (
      <section className="configurations-detail-view configuration-detail-view panel-card">
        <p>Configuration not found.</p>
        <CcButton variant="ghost" onClick={onBack}>
          ← Configurations
        </CcButton>
      </section>
    );
  }

  const recommendation = config.recommendation;
  const operatingPoint = buildDetailOperatingPointView(recommendation);
  const comparison = buildDetailCoilComparisonView(recommendation);
  const equipment = buildDetailEquipmentCardView(recommendation);
  const basis = buildDetailCalculatedBasisView(recommendation);
  const evaluation = buildDetailCatalogueEvaluationView(recommendation);

  const comparisonRows = [
    {
      key: "numberOfTurns",
      label: "Number of Turns",
      glossaryKey: "numberOfTurns",
      optimum: comparison.optimum.numberOfTurns,
      catalogue: comparison.catalogueMatch.numberOfTurns,
    },
    {
      key: "turnSpacing",
      label: "Turn Spacing, mm",
      glossaryKey: "turnSpacing",
      optimum: comparison.optimum.turnSpacingMm,
      catalogue: comparison.catalogueMatch.turnSpacingMm,
    },
    {
      key: "coilCoverage",
      label: "Coil Coverage, mm",
      glossaryKey: "coilCoverage",
      optimum: comparison.optimum.coilCoverageMm,
      catalogue: comparison.catalogueMatch.coilCoverageMm,
    },
    {
      key: "coilSection",
      label: "Coil Section, mm",
      glossaryKey: "coilSection",
      optimum: comparison.optimum.coilSectionMm,
      catalogue: comparison.catalogueMatch.coilSectionMm,
    },
    {
      key: "heatingLength",
      label: "Heating Length, mm",
      glossaryKey: "heatingLength",
      optimum: comparison.optimum.heatingLengthMm,
      catalogue: comparison.catalogueMatch.heatingLengthMm,
    },
  ];

  const advisoryCauses = recommendation.advisoryCauses.map((item) => ({
    code: item.code,
    message: item.message,
  }));
  const advisoryActions = recommendation.advisoryActions.map((item) => ({
    code: item.code,
    message: item.message,
  }));

  const handleRecalculate = () => {
    setRecalculateOpen(true);
  };

  return (
    <>
    <section className="configurations-detail-view configuration-detail-view">
      <nav className="detail-breadcrumb" aria-label="Breadcrumb">
        <CcButton variant="ghost" onClick={onBack}>
          ← Configurations
        </CcButton>
      </nav>

      <div className={detailStatusBannerClass(recommendation.recommendationStatus)}>
        <div className="detail-status-banner__body">
          <div className="detail-status-banner__identity">
            <h2 className="detail-status-banner__reference">{config.reference}</h2>
            {config.project ? (
              <p className="detail-status-banner__project">{config.project}</p>
            ) : null}
          </div>
          <span className={configurationStatusPillClass(recommendation.recommendationStatus)}>
            <span className="status-pill__dot" />
            <span>{formatConfigurationStatusLabel(recommendation.recommendationStatus)}</span>
          </span>
        </div>
        <div className="detail-status-banner__actions">
          <CcButton variant="secondary" onClick={() => onEdit(config.id)}>
            Edit
          </CcButton>
          <CcButton variant="secondary" onClick={handleRecalculate}>
            Recalculate
          </CcButton>
        </div>
      </div>

      <div className="detail-advisory-section">
        <div className="detail-advisory-card">
          <h3 className="detail-advisory-card__title">Advisory Causes</h3>
          <ul className="detail-advisory-list" aria-label="Advisory causes">
            <AdvisoryList items={advisoryCauses} emptyMessage="No advisory causes" />
          </ul>
        </div>
        <div className="detail-advisory-card">
          <h3 className="detail-advisory-card__title">Advisory Actions</h3>
          <ul className="detail-advisory-list" aria-label="Advisory actions">
            <AdvisoryList items={advisoryActions} emptyMessage="No advisory actions" />
          </ul>
        </div>
      </div>

      <div className="detail-operating-point">
        <div className="detail-operating-point__group">
          <h3 className="detail-operating-point__group-title">Power Source Rated Limits</h3>
          <div className="stat-grid">
            <DetailStatCard
              label="Power Source Rating"
              glossaryKey="powerSourceRating"
              value={operatingPoint.ratedLimits.powerSourceRatingKva}
              unit="kVA"
            />
            <DetailStatCard
              label="Power Source Frequency"
              glossaryKey="powerSourceFrequency"
              value={operatingPoint.ratedLimits.powerSourceFrequencyHz}
              unit="Hz"
            />
            <DetailStatCard
              label="Maximum Power Source Voltage"
              glossaryKey="maximumPowerSourceVoltage"
              value={operatingPoint.ratedLimits.maximumPowerSourceVoltageV}
              unit="V"
            />
            <DetailStatCard
              label="Maximum Power Source Current"
              glossaryKey="maximumPowerSourceCurrent"
              value={operatingPoint.ratedLimits.maximumPowerSourceCurrentA}
              unit="A"
            />
          </div>
        </div>

        <div className="detail-operating-point__group">
          <h3 className="detail-operating-point__group-title">Calculated Operating Point</h3>
          <div className="stat-grid">
            <DetailStatCard
              label="Power Source Voltage"
              glossaryKey="powerSourceVoltage"
              value={operatingPoint.operatingPoint.powerSourceVoltageV}
              unit="V"
            />
            <DetailStatCard
              label="Power Source Current"
              glossaryKey="powerSourceCurrent"
              value={operatingPoint.operatingPoint.powerSourceCurrentA}
              unit="A"
            />
            <DetailStatCard
              label="Power Factor"
              glossaryKey="powerFactor"
              value={operatingPoint.operatingPoint.powerFactor}
            />
            <DetailStatCard
              label="Delivered Power"
              glossaryKey="deliveredPower"
              value={operatingPoint.operatingPoint.deliveredPowerKw}
              unit="kW"
            />
          </div>
        </div>

        <div className="detail-operating-point__group">
          <div className="detail-utilization-band">
            <div className="detail-utilization-band__header">
              <span className="detail-utilization-band__label">
                Power Utilization <InfoAffordance glossaryKey="powerUtilization" />
              </span>
              <span className="detail-utilization-band__value">
                {operatingPoint.utilization.displayValue}
              </span>
            </div>
            <div className="utilization-meter">
              <div className="utilization-meter__track">
                <div className="utilization-meter__band" />
                <div
                  className={`utilization-meter__fill ${operatingPoint.utilization.fillClass}`.trim()}
                  style={{
                    width:
                      operatingPoint.utilization.fillPercent !== null
                        ? `${operatingPoint.utilization.fillPercent}%`
                        : "0%",
                  }}
                />
              </div>
              <div className="utilization-meter__scale">
                <span className="utilization-meter__scale-label utilization-meter__scale-label--0">
                  0%
                </span>
                <span className="utilization-meter__scale-label utilization-meter__scale-label--55">
                  55%
                </span>
                <span className="utilization-meter__scale-label utilization-meter__scale-label--85">
                  85%
                </span>
                <span className="utilization-meter__scale-label utilization-meter__scale-label--100">
                  100%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="detail-operating-point__group">
          <div className="stat-grid detail-timing">
            <DetailStatCard
              label="Heating Cycle Time"
              glossaryKey="heatingCycleTime"
              value={operatingPoint.timing.heatingCycleTimeMinutes}
              unit="min"
            />
            <DetailStatCard
              label="Cooldown Time"
              glossaryKey="cooldownTime"
              value={operatingPoint.timing.cooldownTimeMinutes ?? "—"}
              unit={operatingPoint.timing.cooldownTimeMinutes != null ? "min" : undefined}
            />
          </div>
        </div>
      </div>

      <div className="detail-coil-comparison">
        <div className="detail-comparison-table-wrap">
          <h3 className="detail-section-title">Optimum vs Catalogue-Matched Coil</h3>
          <table
            className="detail-comparison-table"
            aria-label="Optimum versus catalogue-matched coil comparison"
          >
            <thead>
              <tr>
                <th scope="col">Metric</th>
                <th scope="col">Optimum</th>
                <th scope="col">Catalogue Match</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row) => (
                <tr key={row.key}>
                  <th scope="row">
                    <span className="detail-comparison-metric-label">
                      {row.label} <InfoAffordance glossaryKey={row.glossaryKey} />
                    </span>
                  </th>
                  <td>{row.optimum}</td>
                  <td>{row.catalogue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="detail-equipment-card">
          <h3 className="detail-section-title">Selected Equipment</h3>
          <dl className="detail-equipment-list">
            <div className="detail-equipment-row">
              <dt>Power Source</dt>
              <dd>{equipment.powerSourceName}</dd>
            </div>
            <div className="detail-equipment-row">
              <dt>Coil Identifier</dt>
              <dd>{equipment.coilIdentifier}</dd>
            </div>
            <div className="detail-equipment-row">
              <dt>Coil Frame Size, in</dt>
              <dd>{equipment.coilFrameSizeInches}</dd>
            </div>
            <div className="detail-equipment-row">
              <dt>Effective Coil Inside Diameter, mm</dt>
              <dd>{equipment.effectiveCoilInsideDiameterMm}</dd>
            </div>
            <div className="detail-equipment-row">
              <dt>Number of Turns</dt>
              <dd>{equipment.numberOfTurns}</dd>
            </div>
            <div className="detail-equipment-row">
              <dt>Turn Spacing, mm</dt>
              <dd>{equipment.turnSpacingMm}</dd>
            </div>
            <div className="detail-equipment-row">
              <dt>Coil Coverage, mm</dt>
              <dd>{equipment.coilCoverageMm}</dd>
            </div>
            <div className="detail-equipment-row">
              <dt>Coil Section, mm</dt>
              <dd>{equipment.coilSectionMm}</dd>
            </div>
            <div className="detail-equipment-row">
              <dt>Power Source Output Limiting</dt>
              <dd className={equipment.outputLimitingApplied ? "detail-equipment-output-limiting--applied" : ""}>
                {equipment.outputLimitingLabel}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="detail-coil-schematic">
        <h3 className="detail-section-title">Coil Layout Schematic</h3>
        <div
          className="detail-schematic-svg-container"
          aria-label="Coil layout schematic diagram"
          {...(schematicSvg
            ? { dangerouslySetInnerHTML: { __html: schematicSvg } }
            : { children: null })}
        />
      </div>

      <div className="detail-basis-section">
        <div className="detail-basis-panel">
          <h3 className="detail-section-title">Calculated Basis</h3>
          <dl className="detail-basis-grid" aria-label="Calculated basis values">
            <div className="detail-basis-row">
              <dt>
                Factory Coating Outside Diameter{" "}
                <InfoAffordance glossaryKey="factoryCoatingOutsideDiameter" />
              </dt>
              <dd>
                <span className="mono-value">{basis.factoryCoatingOutsideDiameterMm}</span>{" "}
                <span className="detail-basis-unit">mm</span>
              </dd>
            </div>
            <div className="detail-basis-row">
              <dt>
                Bare Steel Length <InfoAffordance glossaryKey="bareSteelLength" />
              </dt>
              <dd>
                <span className="mono-value">{basis.bareSteelLengthMm}</span>{" "}
                <span className="detail-basis-unit">mm</span>
              </dd>
            </div>
            <div className="detail-basis-row">
              <dt>
                Heating Length <InfoAffordance glossaryKey="heatingLength" />
              </dt>
              <dd>
                <span className="mono-value">{basis.heatingLengthMm}</span>{" "}
                <span className="detail-basis-unit">mm</span>
              </dd>
            </div>
            <div className="detail-basis-row">
              <dt>
                Target Heat Temperature <InfoAffordance glossaryKey="targetHeatTemperature" />
              </dt>
              <dd>
                <span className="mono-value">{basis.targetHeatTemperatureC}</span>{" "}
                <span className="detail-basis-unit">°C</span>
              </dd>
            </div>
            <div className="detail-basis-row">
              <dt>
                Frequency Heating Factor <InfoAffordance glossaryKey="frequencyHeatingFactor" />
              </dt>
              <dd>
                <span className="mono-value">{basis.frequencyHeatingFactor}</span>
              </dd>
            </div>
          </dl>
        </div>

        <div className="detail-basis-panel">
          <h3 className="detail-section-title">Catalogue Evaluation</h3>
          <dl className="detail-basis-grid" aria-label="Catalogue evaluation summary">
            <div className="detail-basis-row">
              <dt>Standard Configurations Generated</dt>
              <dd>
                <span className="mono-value">{evaluation.standardConfigurationsGenerated}</span>
              </dd>
            </div>
            <div className="detail-basis-row">
              <dt>Configurations That Fit</dt>
              <dd>
                <span className="mono-value">{evaluation.configurationsThatFit}</span>
              </dd>
            </div>
            <div className="detail-basis-row">
              <dt>Configuration Selected</dt>
              <dd>
                <span className="mono-value">{evaluation.configurationSelected}</span>
              </dd>
            </div>
            <div className="detail-basis-row">
              <dt>Custom Coil Design Review Advised</dt>
              <dd>
                <span className="mono-value">{evaluation.customCoilDesignReviewAdvised}</span>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </section>

    <ConfirmDialog
      open={recalculateOpen}
      title="Recalculate"
      message="Recalculating will update the recommendation using the current catalogue data and assumptions."
      confirmLabel="Recalculate"
      onClose={() => setRecalculateOpen(false)}
      onConfirm={() => {
        onRecalculate(config.id);
        setRecalculateOpen(false);
      }}
    />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  config: selectSelectedConfiguration(state),
});

const mapDispatchToProps = {
  onBack: closeConfigurationDetail,
  onEdit: (id: string) => openConfigModal("edit", id),
  onRecalculate: recalculateConfiguration,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfigurationDetail);
